/**
 * Connector PATCH mutation (P47.2 #580 + P49.2 #579).
 *
 * Calls PATCH /v1/admin/twin/connectors/{id} with any subset of
 * {enabled, cadence_seconds}. Cache invalidates the connectors
 * list + the per-id run history on success.
 *
 * Backwards-compatible exports:
 *   useToggleConnectorEnabled (P47) -- alias of useUpdateConnector
 *   ToggleInput / ToggleOutput      -- alias of UpdateConnectorInput
 *                                       / UpdateConnectorOutput
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { api } from "shared/api/client";

export interface UpdateConnectorInput {
  connectorId: string;
  enabled?: boolean;
  cadence_seconds?: number | null;
  // P60.1 (#551): per-instance non-secret config. Whole-object
  // replacement -- caller sends the full intended object.
  config?: Record<string, unknown> | null;
}

export interface UpdateConnectorOutput {
  connector_id: string;
  connector_kind: string;
  enabled: boolean;
  cadence_seconds: number | null;
  config: Record<string, unknown> | null;
  enabled_changed: boolean;
  cadence_seconds_changed: boolean;
  config_changed: boolean;
}

export function useUpdateConnector(): UseMutationResult<
  UpdateConnectorOutput,
  Error,
  UpdateConnectorInput
> {
  const qc = useQueryClient();
  return useMutation<UpdateConnectorOutput, Error, UpdateConnectorInput>({
    mutationFn: ({ connectorId, ...patch }: UpdateConnectorInput) =>
      api.patch<UpdateConnectorOutput>(
        `/v1/admin/twin/connectors/${connectorId}`,
        patch,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["admin-connectors"] });
      void qc.invalidateQueries({
        queryKey: ["admin-connector-runs", vars.connectorId],
      });
    },
  });
}

// P47 alias: callers using the old name still compile.
export const useToggleConnectorEnabled = useUpdateConnector;
